import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import { LinkSimpleIcon } from "@/components/icons/link-simple-icon";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { EXTRA_FOOD_DEFAULT_MEAL_TIME } from "@/features/meal-plan/constants/extra-food";
import { useAddYourFood } from "@/hooks/requests/your-food/use-add-your-food";
import { useToast } from "@/hooks/ui/use-toast";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { memo, RefObject, useCallback, useState } from "react";
import { AddFromLinkModal } from "../add-from-link-modal";
import { SaveMealAsModal } from "../save-meal-as-modal";
import { AddFoodModalMode, buildRecipeProcessingRoute } from "./recipe-processing-route";

interface LinkOptionProps {
  pageRef: RefObject<HTMLElement>;
  disabled?: boolean;
  onComplete: () => void;
  mode?: AddFoodModalMode;
  day?: string;
  targetDate?: string;
  mealTime?: Body_add_your_food_api_v1_meals_your_food_post.meal_time;
}

export const LinkOption = memo(function LinkOption({
  pageRef,
  disabled,
  onComplete,
  mode = "default",
  day,
  targetDate,
  mealTime,
}: LinkOptionProps) {
  const router = useIonRouter();
  const { addYourFoodAsync, isLoading } = useAddYourFood();
  const { showError } = useToast();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isSaveMealModalOpen, setIsSaveMealModalOpen] = useState(false);
  const [submittedUrl, setSubmittedUrl] = useState<string>("");

  const handleClick = useCallback(() => {
    setIsLinkModalOpen(true);
  }, []);

  const handleLinkDismiss = useCallback(() => {
    setIsLinkModalOpen(false);
  }, []);

  const submitUrl = useCallback(
    async (
      url: string,
      submitMealTime: Body_add_your_food_api_v1_meals_your_food_post.meal_time,
    ) => {
      try {
        const data = await addYourFoodAsync({
          url,
          meal_time: submitMealTime,
        });

        const mealId = data.meal_id;
        setIsSaveMealModalOpen(false);
        setSubmittedUrl("");
        onComplete();
        router.push(
          buildRecipeProcessingRoute({
            mealId,
            mode,
            day,
            targetDate,
            mealTime: submitMealTime,
          }),
          "forward",
          "replace",
        );
      } catch (error) {
        showError("Failed to add your food. Please try again.");
        console.error("Error adding your food from URL:", error);
      }
    },
    [addYourFoodAsync, onComplete, router, mode, day, targetDate, showError],
  );

  const handleSubmit = useCallback(
    (url: string) => {
      setIsLinkModalOpen(false);

      if (mode === "extra-food") {
        void submitUrl(url, EXTRA_FOOD_DEFAULT_MEAL_TIME);
        return;
      }

      if (mode === "add-to-meal-time" && mealTime) {
        void submitUrl(url, mealTime);
        return;
      }

      setSubmittedUrl(url);
      setIsSaveMealModalOpen(true);
    },
    [mode, mealTime, submitUrl],
  );

  const handleSelectMealTime = useCallback(
    async (mealTime: Body_add_your_food_api_v1_meals_your_food_post.meal_time) => {
      await submitUrl(submittedUrl, mealTime);
    },
    [submitUrl, submittedUrl],
  );

  const handleCancelMealTime = useCallback(() => {
    setIsSaveMealModalOpen(false);
    setSubmittedUrl("");
  }, []);

  return (
    <>
      <OutlinedButton
        color="black"
        textCase="normal"
        disabled={disabled || isLoading}
        onClick={handleClick}
        className="flex items-center justify-between px-3"
      >
        <LinkSimpleIcon className="h-6 w-6" />
        <span className={textVariants.bodySingleLine}>Add TikTok, Instagram, or link</span>
        <div className="h-6 w-6" />
      </OutlinedButton>

      <AddFromLinkModal
        pageRef={pageRef}
        isOpen={isLinkModalOpen}
        onDismiss={handleLinkDismiss}
        onSubmit={handleSubmit}
      />

      {mode === "default" && (
        <SaveMealAsModal
          isOpen={isSaveMealModalOpen}
          disabled={isLoading}
          onDismiss={handleCancelMealTime}
          onSelectMealTime={handleSelectMealTime}
        />
      )}
    </>
  );
});
